// settings/settingsApi.ts
import apiClient from '@/lib/api-client';


// ==================== BRANCHES ====================
export async function getBranches(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/branches`);
    return data.data;
}

export async function createBranch(tenantId: string, data: {
    name: string; code: string; businessId: string;
    address?: string; phone?: string; email?: string; isMainBranch?: boolean;
}) {
    const response = await apiClient.post(`/tenants/${tenantId}/branches`, data);
    return response.data?.data;
}

export async function updateBranch(tenantId: string, branchId: string, payload: any) {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/branches/${branchId}`, payload);
    return data.data;
}

// ==================== TABLES ====================
export async function getTables(tenantId: string, branchId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/branches/${branchId}/tables`);
    return data.data;
}

export async function createTable(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/tables`, payload);
    return data.data;
}

// ==================== STAFF ====================
export async function getStaff(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/users`);
    // Handle both paginated and non-paginated response shapes
    if (data.data?.users) return data.data.users; // Paginated: { success, data: { users: [...] } }
    if (Array.isArray(data.data)) return data.data; // Standard: { success, data: [...] }
    if (Array.isArray(data)) return data; // Direct array
    return [];
}

export async function createStaff(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/users`, payload);
    return data.data;
}

export async function updateStaff(tenantId: string, staffId: string, payload: any) {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/users/${staffId}`, payload);
    return data.data;
}

// Add these to settingsApi.ts:

export async function deactivateStaff(tenantId: string, staffId: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/users/${staffId}/deactivate`);
}

export async function reactivateStaff(tenantId: string, staffId: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/users/${staffId}/reactivate`);
}

export async function resetStaffPassword(tenantId: string, staffId: string, newPassword: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/users/${staffId}/reset-password`, { newPassword });
}

export async function setStaffPin(tenantId: string, staffId: string, pin: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/users/${staffId}/set-pin`, { pin });
}

export async function getStaffPermissions(tenantId: string, staffId: string): Promise<any> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/users/${staffId}/permissions`);
    return data?.data || data;
}

export async function getStaffActivity(tenantId: string, staffId: string, limit?: number): Promise<any[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/users/${staffId}/activity`, { params: { limit: limit || 50 } });
    return data?.data || [];
}

export async function getStaffShifts(tenantId: string, staffId: string, limit?: number): Promise<any[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/users/${staffId}/shifts`, { params: { limit: limit || 30 } });
    return data?.data || [];
}

// ==================== ROLES ====================
export async function getRoles(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/roles`);
    return data.data;
}

export async function createRole(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/roles`, payload);
    return data.data;
}

export async function updateRole(tenantId: string, roleId: string, payload: any) {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/roles/${roleId}`, payload);
    return data.data;
}

export async function deleteRole(tenantId: string, roleId: string) {
    await apiClient.delete(`/tenants/${tenantId}/roles/${roleId}`);
}

export async function getRoleDetail(tenantId: string, roleId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/roles/${roleId}`);
    return data.data;
}

export async function getPermissions() {
    const { data } = await apiClient.get('/permissions');
    return data.data;
}

// ==================== ROLES (continued) ====================

// Assign role to a user
export async function assignRoleToUser(tenantId: string, userId: string, roleId: string) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/users/${userId}/roles`, { userId, roleId });
    return data.data;
}

// Remove role from a user
export async function removeRoleFromUser(tenantId: string, userId: string, roleId: string) {
    const { data } = await apiClient.delete(`/tenants/${tenantId}/users/${userId}/roles`, {
        data: { userId, roleId },
    });
    return data.data;
}

// Get roles assigned to a specific user
export async function getUserRoles(tenantId: string, userId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/users/${userId}/roles`);
    return data.data;
}

// ==================== USER PERMISSION OVERRIDES ====================

/**
 * Grant a permission directly to a user (explicit ALLOW).
 * This gives the user a permission even if their roles don't provide it.
 */
export async function grantUserPermission(
    userId: string,
    permissionId: string,
    reason?: string
) {
    const { data } = await apiClient.post(`/users/${userId}/permissions/grant`, {
        userId,
        permissionId,
        reason,
    });
    return data.data;
}

/**
 * Revoke a permission from a user (explicit DENY).
 * This removes a permission even if the user's roles provide it.
 */
export async function revokeUserPermission(
    userId: string,
    permissionId: string,
    reason?: string
) {
    const { data } = await apiClient.post(`/users/${userId}/permissions/revoke`, {
        userId,
        permissionId,
        reason,
    });
    return data.data;
}

/**
 * Remove a user permission override entirely.
 * The user goes back to inheriting permissions purely from their roles.
 */
export async function removeUserPermissionOverride(
    userId: string,
    permissionId: string
) {
    const { data } = await apiClient.delete(`/users/${userId}/permissions/override`, {
        data: { userId, permissionId },
    });
    return data.data;
}

/**
 * Get all explicit permission overrides for a user.
 * Returns { granted: true/false } for each overridden permission.
 */
export async function getUserPermissionOverrides(userId: string) {
    const { data } = await apiClient.get(`/users/${userId}/permissions/overrides`);
    return data.data;
}

/**
 * Get the CURRENTLY AUTHENTICATED user's effective permissions, roles, and overrides.
 * This is the "What can I do?" endpoint — call it after login or on app init.
 */
export async function getMyPermissions() {
    const { data } = await apiClient.get('/auth/me/permissions');
    return data.data;
}

// ==================== TAXES ====================
export async function getTaxes(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/taxes`);
    return data.data;
}

export async function createTax(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/taxes`, payload);
    return data.data;
}

export async function updateTax(tenantId: string, taxId: string, payload: any) {
    const { data } = await apiClient.put(`/tenants/${tenantId}/taxes/${taxId}`, payload);
    return data.data;
}

export async function deleteTax(tenantId: string, taxId: string) {
    await apiClient.delete(`/tenants/${tenantId}/taxes/${taxId}`);
}

export async function setDefaultTax(tenantId: string, taxId: string) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/taxes/${taxId}/default`);
    return data;
}

// ==================== PAYMENT METHODS ====================
export async function getPaymentMethods(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/payment-methods`);
    return data.data;
}

export async function createPaymentMethod(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/payment-methods`, payload);
    return data.data;
}

export async function updatePaymentMethod(tenantId: string, methodId: string, payload: any) {
    const { data } = await apiClient.put(`/tenants/${tenantId}/payment-methods/${methodId}`,
        { ...payload, id: methodId }  // ✅ Add id to the request body
    );
    return data?.data || data;
}

export async function deletePaymentMethod(tenantId: string, methodId: string) {
    await apiClient.delete(`/tenants/${tenantId}/payment-methods/${methodId}`);
}

// ==================== CURRENCIES ====================
export async function getCurrencies(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/currencies`);
    return data.data;
}

export async function createCurrency(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/currencies`, payload);
    return data.data;
}

export async function updateCurrency(tenantId: string, currencyId: string, payload: any) {
    const { data } = await apiClient.put(`/tenants/${tenantId}/currencies/${currencyId}`, payload);
    return data.data;
}

export async function deleteCurrency(tenantId: string, currencyId: string) {
    await apiClient.delete(`/tenants/${tenantId}/currencies/${currencyId}`);
}

export async function setBaseCurrency(tenantId: string, currencyId: string) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/currencies/${currencyId}/base`);
    return data;
}

// ==================== BANK ACCOUNTS ====================
export async function getBankAccounts(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/bank-accounts`);
    return data.data;
}

export async function createBankAccount(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/bank-accounts`, payload);
    return data.data;
}

// ==================== POS TERMINALS ====================
export async function getPosTerminals(tenantId: string, params?: any) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/pos-terminals`, { params });
    return data.data;
}

export async function createPosTerminal(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/pos-terminals`, payload);
    return data.data;
}

// ==================== RECEIPT TEMPLATES ====================
export async function getReceiptTemplates(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/receipt-templates`);
    return data.data;
}

export async function createReceiptTemplate(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/receipt-templates`, payload);
    return data.data;
}

export async function setDefaultReceiptTemplate(tenantId: string, templateId: string, type: string) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/receipt-templates/${templateId}/default/${type}`);
    return data;
}

// ==================== NUMBER SEQUENCES ====================
export async function getNumberSequences(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/number-sequences`);
    return data.data;
}

export async function createNumberSequence(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/number-sequences`, payload);
    return data.data;
}

export async function getNextSequenceNumber(tenantId: string, code: string) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/number-sequences/${code}/next`);
    return data.data;
}

// ==================== DISCOUNT REASONS ====================
export async function getDiscountReasons(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/discount-reasons`);
    return data.data;
}

export async function createDiscountReason(tenantId: string, payload: any) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/discount-reasons`, payload);
    return data.data;
}

export async function deleteDiscountReason(tenantId: string, reasonId: string) {
    await apiClient.delete(`/tenants/${tenantId}/discount-reasons/${reasonId}`);
}

// ==================== TENANT ====================
export async function getTenant(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}`);
    return data.data;
}

export async function updateTenant(tenantId: string, payload: any) {
    const { data } = await apiClient.patch(`/tenants/${tenantId}`, payload);
    return data.data;
}

// ==================== BRANCHES ====================
export async function deleteBranch(tenantId: string, branchId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/branches/${branchId}`);
}

// ==================== TABLES ====================
export async function updateTable(tenantId: string, tableId: string, data: any): Promise<any> {
    const { data: response } = await apiClient.patch(`/tenants/${tenantId}/tables/${tableId}`, data);
    return response?.data || response;
}

export async function deleteTable(tenantId: string, tableId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/tables/${tableId}`);
}

// ==================== POS TERMINALS ====================
export async function updatePosTerminal(tenantId: string, terminalId: string, data: any): Promise<any> {
    const { data: response } = await apiClient.patch(`/tenants/${tenantId}/pos-terminals/${terminalId}`, data);
    return response?.data || response;
}

export async function deletePosTerminal(tenantId: string, terminalId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/pos-terminals/${terminalId}`);
}

// ==================== BANK ACCOUNTS ====================
export async function updateBankAccount(tenantId: string, accountId: string, data: any): Promise<any> {
    const { data: response } = await apiClient.patch(`/tenants/${tenantId}/bank-accounts/${accountId}`, data);
    return response?.data || response;
}

export async function deleteBankAccount(tenantId: string, accountId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/bank-accounts/${accountId}`);
}

// ==================== DISCOUNT REASONS ====================
export async function updateDiscountReason(tenantId: string, reasonId: string, payload: any): Promise<any> {
    const { data } = await apiClient.put(`/tenants/${tenantId}/discount-reasons/${reasonId}`, payload);
    return data?.data || data;
}


// ==================== RECEIPT TEMPLATES ====================
export async function updateReceiptTemplate(tenantId: string, templateId: string, payload: any): Promise<any> {
    const { data } = await apiClient.put(`/tenants/${tenantId}/receipt-templates/${templateId}`, payload);
    return data?.data || data;
}

export async function deleteReceiptTemplate(tenantId: string, templateId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/receipt-templates/${templateId}`);
}

// Fix getBranchStaff - ADD /tenants/ prefix
export async function getBranchStaff(tenantId: string, branchId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/branches/${branchId}/staff`);
    return data?.data || data;
}

// Fix assignStaffToBranch - ADD /tenants/ prefix
export async function assignStaffToBranch(tenantId: string, branchId: string, staffIds: string[]) {
    const { data } = await apiClient.post(`/tenants/${tenantId}/branches/${branchId}/staff`, { staffIds });
    return data?.data || data;
}

export async function getStaffBranches(tenantId: string, staffId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/staff/${staffId}/branches`);
    return data?.data || data;
}

export async function getRolesWithPermissions(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/roles/with-permissions`);
    return data?.data || [];
}

/**
 * Get the current user's POS access WITHOUT requiring users:view permission
 * This uses the /auth/me/pos-permissions endpoint
 * Returns normalized posAccess value
 */
export async function getMyPosPermissions(): Promise<'none' | 'retail' | 'bom' | 'both'> {
    const { data } = await apiClient.get('/auth/me/pos-permissions');
    const responseData = data?.data || data;

    let posAccess = responseData?.posAccess || 'none';

    // Normalize to lowercase string
    if (typeof posAccess === 'string') {
        posAccess = posAccess.toLowerCase();
    } else {
        posAccess = 'none';
    }

    // Map to valid values
    if (!['none', 'retail', 'bom', 'both'].includes(posAccess)) {
        posAccess = 'none';
    }

    return posAccess as 'none' | 'retail' | 'bom' | 'both';
}